import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { evidenceApi, learningHooksApi } from '../../services/api';

interface LearningHook {
  id: number;
  title: string;
}

interface EvidenceQueueItem {
  id: number;
  task: number;
  task_title: string;
  hook_id: number;
  hook_title: string;
  stage: string;
  student: number;
  student_email: string;
  status: string;
  submission_text?: string;
  submitted_at: string | null;
}

export const EvidenceReviewPanel: React.FC = () => {
  const [hooks, setHooks] = useState<LearningHook[]>([]);
  const [selectedHookId, setSelectedHookId] = useState<number | null>(null);
  const [queueStatus, setQueueStatus] = useState<'submitted' | 'approved' | 'needs_revision'>('submitted');
  const [queue, setQueue] = useState<EvidenceQueueItem[]>([]);

  const [loading, setLoading] = useState(true);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const hookTitle = useMemo(() => {
    const h = hooks.find((x) => x.id === selectedHookId);
    return h?.title || '';
  }, [hooks, selectedHookId]);

  const fetchHooks = useCallback(async () => {
    const hooksResponse = await learningHooksApi.getAll();
    const hooksData = hooksResponse.data.learning_hooks || hooksResponse.data || [];
    const minimal = (Array.isArray(hooksData) ? hooksData : []).map((h: any) => ({
      id: h.id,
      title: h.title,
    }));
    setHooks(minimal);
    if (minimal.length > 0 && selectedHookId == null) {
      setSelectedHookId(minimal[0].id);
    }
  }, [selectedHookId]);

  const fetchQueue = useCallback(
    async (hookId: number, status: 'submitted' | 'approved' | 'needs_revision') => {
      setLoadingQueue(true);
      const res = await evidenceApi.reviewQueue({ hook_id: hookId, status });
      setQueue(res.data.evidence || []);
      setLoadingQueue(false);
    },
    [],
  );

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        await fetchHooks();
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Failed to load learning hooks');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [fetchHooks]);

  useEffect(() => {
    const run = async () => {
      if (!selectedHookId) return;
      setError('');
      setSuccess('');
      try {
        await fetchQueue(selectedHookId, queueStatus);
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Failed to load evidence queue');
      }
    };
    run();
  }, [fetchQueue, queueStatus, selectedHookId]);

  const handleReview = async (evidenceId: number, decision: 'approved' | 'needs_revision') => {
    setError('');
    setSuccess('');
    const notes = window.prompt(`Notes for ${decision}:`, '') || '';
    try {
      await evidenceApi.review(evidenceId, { decision, notes });
      setSuccess('Evidence reviewed');
      if (selectedHookId) {
        await fetchQueue(selectedHookId, queueStatus);
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to review evidence');
    }
  };

  if (loading) {
    return <div className="bg-white rounded-lg shadow p-6">Loading…</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Evidence Review</h2>
          <div className="text-sm text-gray-600">
            {hookTitle ? `For: ${hookTitle}` : 'Select a learning hook'}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedHookId ?? ''}
            onChange={(e) => setSelectedHookId(Number(e.target.value) || null)}
            className="border rounded px-2 py-1 text-sm"
          >
            {hooks.map((h) => (
              <option key={h.id} value={h.id}>
                {h.title}
              </option>
            ))}
          </select>

          <select
            value={queueStatus}
            onChange={(e) => setQueueStatus(e.target.value as any)}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value="submitted">submitted</option>
            <option value="approved">approved</option>
            <option value="needs_revision">needs_revision</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">{success}</div>
      )}

      {loadingQueue ? (
        <div className="text-sm text-gray-500">Loading evidence…</div>
      ) : queue.length === 0 ? (
        <div className="text-sm text-gray-500">No evidence found for this filter.</div>
      ) : (
        <div className="space-y-3">
          {queue.map((item) => (
            <div key={item.id} className="border rounded p-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-medium">[{item.stage}] {item.task_title}</div>
                  <div className="text-sm text-gray-600">Student: {item.student_email}</div>
                  <div className="text-xs text-gray-500">Submitted: {item.submitted_at ? new Date(item.submitted_at).toLocaleString() : '—'}</div>
                </div>

                {queueStatus === 'submitted' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleReview(item.id, 'approved')}
                      className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 text-sm"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleReview(item.id, 'needs_revision')}
                      className="bg-yellow-600 text-white px-3 py-1 rounded hover:bg-yellow-700 text-sm"
                    >
                      Needs revision
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-2 text-sm text-gray-800 whitespace-pre-wrap break-words">
                {(() => {
                  const raw = (item.submission_text || '').trim();
                  if (!raw) return '—';
                  const isLink = (raw.startsWith('http://') || raw.startsWith('https://')) && raw.indexOf(' ') === -1;
                  if (!isLink) return raw;
                  return (
                    <a href={raw} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                      {raw}
                    </a>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
